import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { contactUsApi } from "../../Redux/Redux-slices/contactSlice";
import { useNotification } from "../../contextApi/NotificationProvider";
import { countryData } from "../../utils/countryCode";
import { Helmet } from "react-helmet-async";
import {
  Calendar,
  Clock,
  Users,
  Shield,
  CheckCircle,
  Star,
  Award,
  TrendingUp,
  Zap,
  ArrowRight,
  PlayCircle,
} from "lucide-react";
import demoVideo from "../../Assets/demoVideo.mp4";

const ScheduleDemo = () => {
  const [mobileRegex, setMobileRegex] = useState("");
  const dispatch = useDispatch();
  const { showNotification } = useNotification();
  const { loading } = useSelector((state) => state.contact);
  const [captchaQuestion, setCaptchaQuestion] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [correctAnswer, setCorrectAnswer] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    generateCaptcha();
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      countryCode: "+91",
    },
  });

  const generateCaptcha = () => {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 5; i++) {
      result += characters.charAt(
        Math.floor(Math.random() * characters.length)
      );
    }
    setCaptchaQuestion(result);
    setCorrectAnswer(result);
  };

  const onSubmit = async (data) => {
    if (captchaAnswer === "") {
      showNotification(
        "Please complete the security verification",
        "error",
        2500
      );
      return;
    }

    if (captchaAnswer !== correctAnswer) {
      showNotification(
        "Security verification failed. Please try again",
        "error",
        3000
      );
      setCaptchaAnswer("");
      generateCaptcha();
      return;
    }

    const payload = {
      first_name: data?.firstName,
      last_name: data?.lastName,
      email: data?.email,
      mobile: data?.countryCode + data?.phone,
      message: `Company: ${data?.company} | Role: ${
        data?.role || "Not specified"
      }`,
      contact_us_by: "demo",
    };

    try {
      const response = await dispatch(contactUsApi(payload)).unwrap();
      if (response.message === "Your data has been successfully submitted.") {
        showNotification(
          "🎉 Demo scheduled! We'll send calendar invite within 2 hours.",
          "success",
          8000
        );
      } else {
        showNotification(
          "Something went wrong. Please try again.",
          "error",
          5000
        );
      }
    } catch (error) {
      showNotification(
        "Something went wrong. Please try again.",
        "error",
        5000
      );
    }
    reset();
    setCaptchaAnswer("");
    generateCaptcha();
  };

  useEffect(() => {
    const selectedCode = watch("countryCode");
    const country = countryData.find((c) => c.code === selectedCode);
    if (country) {
      setMobileRegex(country.regex);
    }
  }, [watch("countryCode")]);

  const benefits = [
    {
      icon: <Zap style={{ width: "24px", height: "24px" }} />,
      title: "See 99% Accuracy in Action",
      description:
        "Live demo of AI-powered personality assessments that actually work",
    },
    {
      icon: <TrendingUp style={{ width: "24px", height: "24px" }} />,
      title: "Reduce Hiring Time by 75%",
      description:
        "Learn how to streamline your hiring process with AI insights",
    },
    {
      icon: <Award style={{ width: "24px", height: "24px" }} />,
      title: "Custom Implementation Plan",
      description:
        "Get a tailored strategy designed specifically for your company",
    },
  ];

  const testimonials = [
    {
      quote:
        "This demo completely changed how we think about hiring. The ROI was clear from day one.",
      author: "Sarah Johnson",
      role: "VP of Talent • TechCorp",
      rating: 5,
    },
    {
      quote:
        "We reduced our hiring time by 60% and improved candidate quality significantly.",
      author: "Michael Chen",
      role: "HR Director • GrowthLabs",
      rating: 5,
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Schedule Demo | See 99% Accurate AI Hiring in Action - SelfSync
        </title>
        <meta
          name="description"
          content="Book a 30-minute demo to see SelfSync's 99% accurate AI hiring platform. Get personalized insights and custom implementation plan. 260+ companies trust us."
        />
        <meta
          name="keywords"
          content="AI hiring demo, talent assessment demo, schedule demo, SelfSync demo, hiring platform demo"
        />
        <link rel="canonical" href="https://selfsync.ai/schedule-demo" />

        {/* Open Graph tags */}
        <meta
          property="og:title"
          content="Schedule Demo | See 99% Accurate AI Hiring in Action - SelfSync"
        />
        <meta
          property="og:description"
          content="Book a 30-minute demo to see SelfSync's 99% accurate AI hiring platform. Get personalized insights and implementation plan."
        />
        <meta property="og:url" content="https://selfsync.ai/schedule-demo" />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://selfsync.ai/static/media/SelfSync%20Logo.df7570a5489657df44eb.png"
        />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Schedule Demo | See 99% Accurate AI Hiring in Action"
        />
        <meta
          name="twitter:description"
          content="Book a 30-minute demo to see SelfSync's AI hiring platform in action."
        />
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Page Header */}
        <section className="bg-gradient-to-br from-purple-500 to-cyan-500 text-white py-6 pt-24 text-center">
          <div className="max-w-4xl mx-auto px-6">
            <div className="mb-4">
              <span className="bg-gradient-to-r from-amber-400 to-amber-500 text-purple-700 md:py-2 max-md:px-2 py-2 px-4 rounded-full text-xs font-bold shadow-lg shadow-amber-400/30 inline-flex items-center gap-2">
                🚀 Free 30-minute Demo
                <span className="bg-purple-600 text-white md:py-1 max-md:px-2  px-6  max-md:py-1  rounded-full text-xs my-0.5 md:my-1">
                  No Credit Card Required
                </span>
              </span>
            </div>

            <h1 className="text-2xl md:text-4xl  font-bold mb-4 leading-tight">
              Why 260+ Companies Trust Us
            </h1>

            <p className="md:text-lg opacity-95 leading-relaxed max-w-3xl mx-auto">
              SelfSync is helping companies hire with 99% accuracy. Want in?
              Your 30-minute personalized demo is just a click away.
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-14 max-md:py-8 bg-gray-50">
          <div className="max-w-7xl mx-auto md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-md:gap-3 items-start mx-2">
              {/* Left Side - Video & Benefits */}
              <div className="flex flex-col gap-8 ">
                {/* Video Section */}
                <div className="bg-white rounded-2xl p-10 max-md:p-6 shadow-xl border border-gray-200 ">
                  <h2 className="text-3xl font-bold text-gray-800 mb-8 max-md:mb-4 text-center">
                    Smarter Hiring Starts Here
                  </h2>

                  <div className="relative w-full pb-[56.25%] bg-gray-100 rounded-xl shadow-lg overflow-hidden">
                    <video
                      src={demoVideo}
                      autoPlay
                      controls
                      muted
                      controlsList="nodownload"
                      onContextMenu={(e) => e.preventDefault()}
                      className="absolute top-0 left-0 w-full h-full object-cover"
                    />
                  </div>

                  <div className="text-center mt-6 p-5 bg-gray-50 rounded-xl border border-gray-200 ">
                    <div className="flex justify-center items-center gap-6 flex-wrap">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-500">
                          99%
                        </div>
                        <div className="text-sm text-gray-500">
                          Accuracy Rate
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-500">
                          30 min
                        </div>
                        <div className="text-sm text-gray-500">Demo Length</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-500">
                          260+
                        </div>
                        <div className="text-sm text-gray-500">Companies</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* What You'll See */}
                <div className="bg-white rounded-2xl p-10 max-md:p-6 shadow-xl border border-gray-200 ">
                  <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
                    What You'll See in This Demo
                  </h2>

                  <div className="flex flex-col gap-6">
                    {benefits.map((benefit, index) => (
                      <div key={index} className="flex gap-4 items-start">
                        <div className="text-purple-500 bg-gray-100 p-3 rounded-xl flex items-center justify-center min-w-12 h-12">
                          {benefit.icon}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800 mb-2">
                            {benefit.title}
                          </h3>
                          <p className="text-gray-500 leading-relaxed">
                            {benefit.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Side - Demo Form */}
              <div
                id="demo-form"
                className="bg-white rounded-2xl p-10 max-md:p-6 shadow-2xl border border-gray-200 sticky top-6 "
              >
                <div className="text-center mb-8">
                  <Calendar className="w-12 h-12 text-purple-500 mx-auto mb-4" />
                  <h2 className="text-3xl font-bold text-gray-800 mb-2">
                    Schedule Your Demo
                  </h2>
                  <p className="text-gray-500 text-base">
                    We'll send a calendar invite within 2 hours
                  </p>

                  {/* Launch Special */}
                  {/* <div className="bg-gradient-to-r from-amber-100 to-amber-200 p-1 rounded-xl mt-4 border border-amber-500">
                    <span className="text-sm font-semibold text-amber-900">
                      🎁 Launch Special: Get your first 50 assessments FREE
                      (worth $500)
                    </span>
                  </div> */}
                </div>
                <div className="w-full md:px-8 max-w-3xl mx-auto">
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex flex-col gap-4"
                  >
                    {/* Name Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...register("firstName", {
                            required: "First name is required",
                            minLength: { value: 2, message: "Too short" },
                            pattern: {
                              value: /^[A-Za-z]+$/,
                              message: "Letters only",
                            },
                          })}
                          className={`w-full p-3 border-2 rounded-lg text-base transition-colors outline-none box-border ${
                            errors.firstName
                              ? "border-red-500"
                              : "border-gray-200 focus:border-purple-500"
                          }`}
                          placeholder="John"
                        />
                        {errors.firstName && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.firstName.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...register("lastName", {
                            required: "Last name is required",
                            minLength: { value: 2, message: "Too short" },
                            pattern: {
                              value: /^[A-Za-z]+$/,
                              message: "Letters only",
                            },
                          })}
                          className={`w-full p-3 border-2 rounded-lg text-base transition-colors outline-none box-border ${
                            errors.lastName
                              ? "border-red-500"
                              : "border-gray-200 focus:border-purple-500"
                          }`}
                          placeholder="Doe"
                        />
                        {errors.lastName && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.lastName.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Work Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        {...register("email", {
                          required: "Email is required",
                          pattern: {
                            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                            message: "Invalid email format",
                          },
                        })}
                        className={`w-full p-3 border-2 rounded-lg text-base transition-colors outline-none box-border ${
                          errors.email
                            ? "border-red-500"
                            : "border-gray-200 focus:border-purple-500"
                        }`}
                        placeholder="john@company.com"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.email.message}
                        </p>
                      )}
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <select
                          {...register("countryCode", { required: "Required" })}
                          className="md:w-30 w-25 p-3 border-2 border-gray-200 rounded-lg text-base outline-none bg-white"
                        >
                          {countryData.map((country, i) => (
                            <option key={i} value={country.code}>
                              {country.code}
                            </option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          {...register("phone", {
                            required: "Phone is required",
                            pattern: {
                              value: /^[\d-]+$/,
                              message: "Numbers only",
                            },
                            minLength: { value: 6, message: "Too short" },
                            maxLength: { value: 15, message: "Too long" },
                          })}
                          placeholder="555-123-4567"
                          className={`flex-1 p-3 border-2 rounded-lg text-base w-full transition-colors outline-none box-border ${
                            errors.phone
                              ? "border-red-500"
                              : "border-gray-200 focus:border-purple-500"
                          }`}
                        />
                      </div>
                      {(errors.countryCode || errors.phone) && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.countryCode?.message || errors.phone?.message}
                        </p>
                      )}
                    </div>

                    {/* Company */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Company Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        {...register("company", {
                          required: "Company is required",
                        })}
                        className={`w-full p-3 border-2 rounded-lg text-base transition-colors outline-none box-border ${
                          errors.company
                            ? "border-red-500"
                            : "border-gray-200 focus:border-purple-500"
                        }`}
                        placeholder="Acme Corp"
                      />
                      {errors.company && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.company.message}
                        </p>
                      )}
                    </div>

                    {/* Role */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Your Role
                      </label>
                      <select
                        {...register("role")}
                        className="w-full p-3 border-2 border-gray-200 rounded-lg text-base outline-none bg-white box-border"
                      >
                        <option value="">Select Role</option>
                        <option value="HR Director">HR Director</option>
                        <option value="Talent Acquisition">
                          Talent Acquisition
                        </option>
                        <option value="CEO/Founder">CEO/Founder</option>
                        <option value="VP/Manager">VP/Manager</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* Security Verification */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Security Verification{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-3 items-center">
                        <div className="bg-gray-100 p-3 rounded-lg font-mono text-lg tracking-wider border-2 border-dashed border-gray-300 min-w-30 text-center">
                          {captchaQuestion}
                        </div>
                        <input
                          type="text"
                          value={captchaAnswer}
                          onChange={(e) => setCaptchaAnswer(e.target.value)}
                          placeholder="Enter code"
                          className="flex-1 w-full p-3 border-2 border-gray-200 rounded-lg text-base outline-none box-border focus:border-purple-500"
                          required
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={!isValid || loading || !captchaAnswer}
                      className={`w-full py-3 px-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 border-none ${
                        !isValid || loading || !captchaAnswer
                          ? "bg-gray-400 cursor-not-allowed"
                          : "custom-button text-white shadow-lg shadow-purple-500/30 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-purple-500/40"
                      }`}
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Scheduling Demo...
                        </>
                      ) : (
                        <>
                          <Calendar className="w-5 h-5" />
                          Schedule My Demo
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>

                    {/* Trust Footer */}
                    <div className="text-center text-sm text-gray-500 flex flex-col gap-2">
                      <div className="flex items-center justify-center gap-6 flex-wrap">
                        <span className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          No Commitment
                        </span>
                        <span className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          2-Hour Response
                        </span>
                        <span className="flex items-center gap-1">
                          <Shield className="w-4 h-4 text-emerald-500" />
                          100% Secure
                        </span>
                      </div>
                      <p className="m-0">Trusted by 260+ companies worldwide</p>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA Section */}
        <section className="bg-gradient-to-br from-gray-100 to-gray-50 text-white py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-4xl font-bold mb-4 text-black">
              Say Goodbye to Bad Hires{" "}
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              See why leading companies choose our AI hiring platform
            </p>

            {/* Money Back Guarantee */}

            <a
              href="#demo-form"
              className="bg-gradient-to-r from-amber-400 to-amber-500 text-purple-700  py-2 px-2 md:px-8 rounded-xl font-bold text-lg md:text-xl no-underline inline-flex items-center gap-3 shadow-lg shadow-amber-400/40 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-amber-400/50 transition-all"
            >
              <Calendar className="w-6 h-6" />
              Schedule Your Demo Now
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>
      </div>

      {/* Add spinning animation for loading */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default ScheduleDemo;
